import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import request from '@/utils/request'

const TOKEN_KEY = 'ts_token'
const USER_KEY = 'ts_user'
const ROLE_KEY = 'ts_current_role'

/** 解析 JWT payload（base64url → UTF-8 JSON）；解析失败返回 null。 */
function decodeJwtPayload(t) {
  try {
    const seg = String(t || '').split('.')[1]
    if (!seg) return null
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
    const bytes = Uint8Array.from(atob(pad), (c) => c.charCodeAt(0))
    return JSON.parse(new TextDecoder('utf-8').decode(bytes))
  } catch {
    return null
  }
}

/**
 * 令牌是否仍在有效期：无 exp 视为长期有效；已过期（含 5s 时钟容差）即失效。
 * 过期后 isLoggedIn 立刻为 false，路由守卫直接跳登录页，
 * 避免"带过期令牌进页面 → 接口全 401 → 页面拿到 null 再取 .records 报错"。
 */
function isTokenValid(t) {
  if (!t) return false
  const payload = decodeJwtPayload(t)
  if (!payload) return false
  if (!payload.exp) return true
  return payload.exp * 1000 > Date.now() + 5000
}

export const useAuthStore = defineStore('auth', () => {
  // 初始化即剔除过期令牌，避免过期令牌被误判为已登录
  if (!isTokenValid(localStorage.getItem(TOKEN_KEY))) {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(ROLE_KEY)
  }
  const token = ref(localStorage.getItem(TOKEN_KEY) || '')
  const user = ref(JSON.parse(localStorage.getItem(USER_KEY) || 'null'))
  const roles = ref(user.value?.roles || [])
  const currentRole = ref(localStorage.getItem(ROLE_KEY) || (roles.value[0] || ''))

  const isLoggedIn = computed(() => isTokenValid(token.value))

  function persist() {
    if (token.value) localStorage.setItem(TOKEN_KEY, token.value)
    else localStorage.removeItem(TOKEN_KEY)
    localStorage.setItem(USER_KEY, JSON.stringify(user.value || null))
    localStorage.setItem(ROLE_KEY, currentRole.value)
  }

  // 真实登录：POST /api/auth/login，返回 { token, roles, pwdResetRequired }
  // role 可空：非空时后端会校验其是否属于本人角色（角色锁定，纵深防御）
  async function login(username, password, role = null) {
    const body = { username, password }
    if (role) body.role = role
    const env = await request.post('/auth/login', body)
    const payload = env.data
    token.value = payload.token
    const profile = {
      id: payload.id ?? null,
      username: payload.username ?? username,
      roles: payload.roles || [],
      pwdResetRequired: payload.pwdResetRequired
    }
    user.value = profile
    roles.value = profile.roles
    currentRole.value = profile.roles[0] || ''
    persist()
    // 重置前端数据范围缓存，确保按新登录账号重新判定学科/年段
    import('@/stores/scope').then(m => m.useScopeStore().reset()).catch(() => {})
    return profile
  }

  // 角色切换（演示态 / 后续接真实数据范围）
  function setRole(role) {
    currentRole.value = role
    localStorage.setItem(ROLE_KEY, role)
  }

  function logout() {
    token.value = ''
    user.value = null
    roles.value = []
    currentRole.value = ''
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(ROLE_KEY)
    // 登出时一并重置数据范围缓存
    import('@/stores/scope').then(m => m.useScopeStore().reset()).catch(() => {})
  }

  return { token, user, roles, currentRole, isLoggedIn, login, setRole, logout }
})
